import React, {
    FC, useState, useMemo, useEffect,
} from 'react';
import { useFragment, useReactiveVar } from '@apollo/client';
import classNames from 'classnames';

import { selectedPeriodVar, subscriptionTypeVar } from 'app/apollo/reaction';

import { useAddToBasketMutation } from 'app/views/Menu/Menu/hooks';
import { useBasketQueryLocate } from 'app/hooks/useBasketQueryLocate';
import formatPrice from 'app/utils/price';
import { useCartMutationInBasket } from 'app/views/Basket/hooks';
import BASKET_ALL_DISHES_FRAGMENT from './graphql/basketAllDishesFragment.graphql';

import css from './PortionsButton.module.scss';
import PlusIcon from './img/plus.component.svg';
import MinusIcon from './img/minus.component.svg';
import XIcon from './img/x.component.svg';

export const PortionsButton: FC<any> = ({
    portions,
    selectedDishPortions,
    price,
    dishId,
    isPriceHidden,
    isHidden,
    isSmall,
    isUpsell,
    selectedDishId,
    isDishWithAdditionalDishesSelect = false,
    serviceCallback = () => { },
}: any) => {
    const subscriptionType = useReactiveVar(subscriptionTypeVar);

    const { handleAddToBasketMutation } = useAddToBasketMutation();
    const [basketData] = useBasketQueryLocate();
    const [displayPortions, setDisplayPortions] = useState(portions[0]);
    const [isDishAddedToRender, setIsDishAddedToRender] = useState(false);


    const deliveryId = useMemo(
        () => window.location.pathname.split('/').find((e) => Boolean(e) && !Number.isNaN(Number(e))),
        [],
    );

    const { mutateItemInCart } = useCartMutationInBasket({ delivery_Id: deliveryId });

    const isDishAdded = useMemo(
        () => {
            const flatSection = basketData?.cart?.sections
                .reduce((acc: any[], section: any) => [...acc, ...section.items], [])
                .map((cartItem: any) => cartItem.dish_id);
            return flatSection?.includes(dishId) || false;
        },
        [dishId, basketData],
    );

    const selectedDishAddedPortions = useMemo(
        () => {
            const flatSection = basketData?.cart?.sections
                .reduce((acc: any[], section: any) => [...acc, ...section.items], [])
                .map((cartItem: any) => ({
                    id: cartItem.dish_id,
                    portions: cartItem.portions,
                }));
            return flatSection?.find((d: any) => d.id === selectedDishId)?.portions || 0;
        },
        [selectedDishId, basketData],
    );

    const portionsToShow = useMemo(
        () => {
            if (isDishAdded) {
                const cartItem = basketData?.cart?.sections
                    .reduce((acc: any[], section: any) => [...acc, ...section.items], [])
                    .find((dish: any) => dish.dish_id === dishId);
                return cartItem.portions;
            }
            return portions[0];
        },
        [portions, isDishAdded, dishId, basketData],
    );

    const portionsToRender = useMemo(
        () => (displayPortions < portions[0]
            ? portions[0]
            : displayPortions),
        [displayPortions, portions],
    );

    const symbolsQuantity = useMemo(
        () => portionsToRender.toString().length,
        [portionsToRender],
    );

    useEffect(
        () => {
            setDisplayPortions(portionsToShow);
        },
        [portionsToShow],
    );

    useEffect(
        () => {
            setIsDishAddedToRender(isDishAdded);
        },
        [isDishAdded],
    );

    const handleAddMorePortions = (e: any) => {
        e.stopPropagation();
        serviceCallback(true);
        const nextPortionsValueIndex = portions
            .findIndex((portion: number) => portion === portionsToShow);
        const nextPortionsValue = portions[nextPortionsValueIndex + 1] || portions[nextPortionsValueIndex];

        const portionsToAdd = isDishAdded
            ? nextPortionsValue
            : portions[0];

        setDisplayPortions(portionsToAdd);
        setIsDishAddedToRender(portionsToAdd > 0);

        const rootAndUpsellDishesPair = localStorage.getItem(`upsellDishes:${dishId}:${selectedDishId}`);

        if (!isDishWithAdditionalDishesSelect) {
            handleAddToBasketMutation({
                dishId: selectedDishId,
                portion: portionsToAdd,
                deliveryId,
            });
            return;
        }
        if (selectedDishId && !rootAndUpsellDishesPair) {
            const selectedDishPortionsIndex = selectedDishPortions
                .findIndex((portion: number) => portion === selectedDishAddedPortions) + 1;
            mutateItemInCart(
                // @ts-ignore
                {
                    dishes: [
                        { dish_id: dishId, portions: portionsToAdd },
                        { dish_id: selectedDishId, portions: selectedDishPortions[selectedDishPortionsIndex] },
                    ],
                },
            );
            localStorage.setItem(`upsellDishes:${dishId}:${selectedDishId}`, 'added');
        } else {
            handleAddToBasketMutation({
                dishId,
                portion: portionsToAdd,
                deliveryId,
            });
        }
    };

    const handleRemovePortions = (e: any) => {
        e.stopPropagation();
        const nextPortionsValueIndex = portions
            .findIndex((portion: number) => portion === portionsToShow);
        const nextPortionsValue = portions[nextPortionsValueIndex - 1] || 0;
        setDisplayPortions(nextPortionsValue);
        setIsDishAddedToRender(nextPortionsValue > 0);

        if (!isDishWithAdditionalDishesSelect) {
            handleAddToBasketMutation({
                dishId: selectedDishId,
                portion: nextPortionsValue,
                deliveryId,
            });
            return;
        }
        if (selectedDishId && nextPortionsValue === 0) {
            const selectedDishPortionsIndex = selectedDishPortions
                .findIndex((portion: number) => portion === selectedDishAddedPortions);
            mutateItemInCart(
                // @ts-ignore
                {
                    dishes: [
                        { dish_id: dishId, portions: nextPortionsValue },
                        {
                            dish_id: selectedDishId,
                            portions: selectedDishPortionsIndex
                                ? selectedDishPortions[selectedDishPortionsIndex - 1]
                                : 0,
                        },
                    ],
                },
            );
            localStorage.removeItem(`upsellDishes:${dishId}:${selectedDishId}`);
        } else {
            handleAddToBasketMutation({
                dishId,
                portion: nextPortionsValue,
                deliveryId,
            });
        }
    };

    const isRecurrent = useMemo(
        () => subscriptionType === 'subscription',
        [subscriptionType],
    );
    const rootClasses = classNames({
        [css.portionsButtonRoot]: true,
        [css.small]: isSmall,
        [css.full]: true,
        [css.upsell]: isUpsell,
        [css.transparent]: isHidden && !isDishAddedToRender,
        // [css.widePadding]: !isDishAdded,
    });
    const removeButtonClasses = classNames({
        [css.portionsButtonIcon]: true,
        [css.small]: isSmall,
        [css.upsell]: isUpsell,
        [css.hidden]: !isDishAddedToRender,
        [css.onion]: isDishAddedToRender && !isSmall && !isUpsell,
    });

    const portionsButtonTextContainerClasses = classNames({
        [css.portionsButtonTextContainer]: true,
        [css.transparent]: isHidden && !isDishAddedToRender,
    });

    const addButtonClasses = classNames({
        [css.portionsButtonIcon]: true,
        [css.small]: isSmall,
        [css.upsell]: isUpsell,
        [css.yellow]: !isDishAddedToRender && !isHidden,
        [css.onion]: isDishAddedToRender && !isSmall && !isUpsell,
    });

    const addSingleButtonClasses = classNames({
        [css.portionsButtonIcon]: true,
        [css.single]: true,
        [css.small]: isSmall,
        [css.upsell]: isUpsell,
        [css.transparent]: isDishAddedToRender || !isHidden,
    });


    const plusIconClasses = classNames({
        [css.portionsButtonPlusIcon]: true,
        [css.small]: isSmall,
        [css.upsell]: isUpsell,
        [css.egg]: !isDishAddedToRender && !isHidden,
        [css.onion]: isDishAddedToRender && !isSmall,
    });

    const minusIconClasses = classNames({
        [css.portionsButtonMinusIcon]: true,
        [css.upsell]: isUpsell,
        [css.small]: isSmall,
    });

    const wrapperClasses = classNames({
        [css.removeWrapper]: true,
        [css.hidden]: !isDishAddedToRender,
    });

    const portionsClasses = classNames({
        'other-number-m-medium': true,
        [css.portionsButtonTextDigits]: true,
        [css.portionsDigits]: true,
        [css.grey]: !isPriceHidden,
        [css.oneDigit]: symbolsQuantity === 1,
        [css.twoDigits]: symbolsQuantity === 2,
    });

    useEffect(
        () => {
            serviceCallback(isDishAdded);
        },
        [isDishAdded],
    );

    return (
        <>
            {isUpsell
                ? (
                    <div
                        className={css.singleButtonWrapper}
                        onClick={handleAddMorePortions}
                        role="none"
                    >
                        <div
                            className={addSingleButtonClasses}
                        >
                            <PlusIcon className={plusIconClasses} />
                        </div>
                    </div>
                ) : (
                    <div
                        className={addSingleButtonClasses}
                        onClick={handleAddMorePortions}
                        role="none"
                    >
                        <PlusIcon className={plusIconClasses} />
                    </div>
                )
            }
            <div className={rootClasses}>
                {isUpsell && isDishAddedToRender && (
                    <div className={css.portionsSplitter}>
                        <div
                            onClick={handleRemovePortions}
                            role="none"
                            className={`${css.portionSplitterItem} ${css.left}`}
                        />
                        <div
                            onClick={handleAddMorePortions}
                            role="none"
                            className={`${css.portionSplitterItem} ${css.right}`}
                        />
                    </div>
                )}
                <div className={wrapperClasses}>
                    <div
                        className={removeButtonClasses}
                        onClick={handleRemovePortions}
                        role="none"
                    >
                        <MinusIcon className={minusIconClasses} />
                    </div>
                </div>
                <div className={portionsButtonTextContainerClasses}>
                    {!isPriceHidden && (
                        <>
                            <span className={`other-number-m-medium ${css.portionsButtonTextDigits}`}>
                                {
                                    formatPrice(
                                        isRecurrent
                                            ? Math.ceil(price * 0.9)
                                            : price,
                                    )
                                }
                            </span>
                            <span className={`body-m-semibold ${css.portionsButtonTextLetters}`}>₽</span>
                            <XIcon className={css.portionsButtonXIcon} />
                        </>
                    )}
                    <span className={portionsClasses}>
                        {portionsToRender}
                    </span>
                </div>
                <div
                    className={addButtonClasses}
                    onClick={handleAddMorePortions}
                    role="none"
                >
                    <PlusIcon className={plusIconClasses} />
                </div>
            </div>
        </>
    );
};
